import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign, CreditCard, Smartphone, Wallet, Users, ArrowRight, Check, X, Banknote, Loader2, Plus, User, Phone
} from 'lucide-react';
import CustomerSelector from './CustomerSelector';

const paymentMethods = [
  { id: 'cash', label: 'Cash', icon: Banknote, color: 'bg-emerald-500' },
  { id: 'online', label: 'Online', icon: Smartphone, color: 'bg-blue-500' },
  { id: 'credit', label: 'Credit', icon: Users, color: 'bg-red-500' },
];

export default function PaymentPanel({
  totalAmount = 0,
  onConfirm,
  onCancel,
  loading = false,
  customer,
  customers = [],
  customerLoading = false,
  onCustomerSelect,
  onCustomerClear,
  onCreateCustomer,
  confirmOnEnter = false,
}) {
  const [method, setMethod] = useState('cash');
  const [amountTendered, setAmountTendered] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [splitCash, setSplitCash] = useState('');
  const [splitOnline, setSplitOnline] = useState('');
  const [creditError, setCreditError] = useState('');
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '' });
  const [newCustomerError, setNewCustomerError] = useState('');
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  const amountTenderedValue = parseFloat(amountTendered) || totalAmount;

  const change = useMemo(() => {
    if (method !== 'cash') return 0;
    return Math.max(0, amountTenderedValue - totalAmount);
  }, [method, amountTenderedValue, totalAmount]);

  const shortfall = useMemo(() => {
    if (method !== 'cash') return 0;
    return Math.max(0, totalAmount - amountTenderedValue);
  }, [method, amountTenderedValue, totalAmount]);

  const isPartialPayment = method === 'split';

  const handleConfirm = () => {
    if (method === 'credit') {
      if (!customer) {
        setCreditError('Please select a customer for the credit sale.');
        return;
      }
      const paymentData = {
        method,
        totalAmount,
        customerId: customer.customerId || customer.id || customer._id,
        customerName: customer.fullName || customer.name,
      };
      onConfirm?.(paymentData);
      return;
    }

    const paymentData = {
      method,
      totalAmount,
    };

    if (method === 'cash') {
      paymentData.amountTendered = amountTenderedValue;
      paymentData.change = change;
      paymentData.shortfall = shortfall;
    } else if (method === 'split') {
      paymentData.cashAmount = parseFloat(splitCash) || 0;
      paymentData.onlineAmount = parseFloat(splitOnline) || 0;
    } else {
      paymentData.referenceNo = referenceNo;
    }

    onConfirm?.(paymentData);
  };

  const handleCustomerSelect = (c) => {
    onCustomerSelect?.(c);
    setCreditError('');
  };

  const handleSaveNewCustomer = async () => {
    const name = newCustomer.name.trim();
    const phone = newCustomer.phone.trim();
    if (!name) {
      setNewCustomerError('Customer name is required');
      return;
    }
    if (!phone) {
      setNewCustomerError('Phone number is required');
      return;
    }
    setCreatingCustomer(true);
    setNewCustomerError('');
    try {
      const created = await onCreateCustomer?.({ fullName: name, phoneNumber: phone });
      if (created) handleCustomerSelect(created);
      setShowAddCustomer(false);
      setNewCustomer({ name: '', phone: '' });
    } catch (err) {
      setNewCustomerError(err?.message || 'Failed to create customer');
    } finally {
      setCreatingCustomer(false);
    }
  };

  const canConfirm = useMemo(() => {
    if (loading) return false;
    if (totalAmount <= 0) return false;
    if (method === 'cash') {
      if (amountTendered === '') return true;
      const amt = parseFloat(amountTendered);
      return !isNaN(amt) && amt > 0;
    }
    if (method === 'credit') {
      return true;
    }
    if (method === 'split') {
      const cash = parseFloat(splitCash) || 0;
      const online = parseFloat(splitOnline) || 0;
      return cash + online >= totalAmount && cash > 0 && online > 0;
    }
    return true;
  }, [loading, totalAmount, method, amountTendered, customer, splitCash, splitOnline]);

  // Keyboard mode: Enter confirms the payment exactly like clicking the button.
  useEffect(() => {
    if (!confirmOnEnter) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      e.stopPropagation();
      if (canConfirm) handleConfirm();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [confirmOnEnter, canConfirm]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Payment</h3>
        <button
          onClick={onCancel}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      <div className="mb-6">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Due</p>
        <p className="text-3xl font-bold text-gray-900 dark:text-white">Rs. {totalAmount.toLocaleString()}</p>
      </div>

      <div className="grid grid-cols-5 gap-2 mb-6">
        {paymentMethods.map((pm) => {
          const selected = method === pm.id;
          return (
            <motion.button
              key={pm.id}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                setMethod(pm.id);
                if (pm.id !== 'cash') setAmountTendered('');
                if (pm.id !== 'credit') setReferenceNo('');
              }}
              className={`flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl transition-all duration-200 border-2 ${
                selected
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <pm.icon size={20} className={selected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'} />
              <span className={`text-[10px] font-medium ${selected ? 'text-primary-700 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {pm.label}
              </span>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {method === 'cash' && (
          <motion.div
            key="cash"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Amount Tendered</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <DollarSign size={16} />
                </div>
                <input
                  type="number"
                  value={amountTendered}
                  onChange={(e) => setAmountTendered(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                  autoFocus
                />
              </div>
            </div>
            {change > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg"
              >
                <div>
                  <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Change Kept (Profit)</span>
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-500">Extra amount will be added to profit</p>
                </div>
                <span className="text-lg font-bold text-emerald-700 dark:text-emerald-400">Rs. {change.toLocaleString()}</span>
              </motion.div>
            )}
            {shortfall > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg"
              >
                <div>
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-400">Shortfall (Discount)</span>
                  <p className="text-[11px] text-amber-600 dark:text-amber-500">Lower amount accepted — will reduce today's profit</p>
                </div>
                <span className="text-lg font-bold text-amber-700 dark:text-amber-400">Rs. {shortfall.toLocaleString()}</span>
              </motion.div>
            )}
          </motion.div>
        )}

        {method === 'credit' && (
          <motion.div
            key="credit"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Customer</label>
              {showAddCustomer ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-2.5 border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/40"
                >
                  <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Plus size={15} className="text-primary-600 dark:text-primary-400" />
                    Add New Customer
                  </p>
                  <div className="relative">
                    <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={newCustomer.name}
                      onChange={(e) => setNewCustomer((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Customer name"
                      className={`w-full pl-8 pr-3 py-2 text-sm bg-white dark:bg-gray-800 border rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400 ${
                        newCustomerError && !newCustomer.name.trim() ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      autoFocus
                    />
                  </div>
                  <div className="relative">
                    <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="Phone number"
                      className={`w-full pl-8 pr-3 py-2 text-sm bg-white dark:bg-gray-800 border rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400 ${
                        newCustomerError && !newCustomer.phone.trim() ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                  </div>
                  {newCustomerError && (
                    <p className="text-xs text-red-500 dark:text-red-400">{newCustomerError}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveNewCustomer}
                      disabled={creatingCustomer}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creatingCustomer && <Loader2 size={14} className="animate-spin" />}
                      Save Customer
                    </button>
                    <button
                      onClick={() => { setShowAddCustomer(false); setNewCustomerError(''); }}
                      disabled={creatingCustomer}
                      className="flex-1 px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </motion.div>
              ) : (
                <CustomerSelector
                  customers={customers}
                  selectedCustomer={customer}
                  onSelect={handleCustomerSelect}
                  onClear={() => { onCustomerClear?.(); setCreditError(''); }}
                  onCreateNew={() => setShowAddCustomer(true)}
                  loading={customerLoading}
                  placeholder="Select customer"
                />
              )}
            </div>

            {creditError && (
              <motion.p
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2.5 rounded-lg"
              >
                {creditError}
              </motion.p>
            )}

            <p className="text-xs text-gray-400 dark:text-gray-500">
              This amount will be added to the customer's credit balance.
            </p>
          </motion.div>
        )}

        {method === 'split' && (
          <motion.div
            key="split"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Cash Amount</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Banknote size={16} />
                </div>
                <input
                  type="number"
                  value={splitCash}
                  onChange={(e) => setSplitCash(e.target.value)}
                  placeholder="Cash amount"
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Online Amount</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Smartphone size={16} />
                </div>
                <input
                  type="number"
                  value={splitOnline}
                  onChange={(e) => setSplitOnline(e.target.value)}
                  placeholder="Online amount"
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                />
              </div>
            </div>
            {(parseFloat(splitCash) + parseFloat(splitOnline)) > 0 && (
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-sm text-gray-500 dark:text-gray-400">Total</span>
                <span className={`text-sm font-bold ${(parseFloat(splitCash) + parseFloat(splitOnline)) >= totalAmount ? 'text-emerald-600' : 'text-red-500'}`}>
                  Rs. {(parseFloat(splitCash) + parseFloat(splitOnline)).toLocaleString()}
                  {(parseFloat(splitCash) + parseFloat(splitOnline)) >= totalAmount ? (
                    <Check size={14} className="inline ml-1" />
                  ) : null}
                </span>
              </div>
            )}
          </motion.div>
        )}

        {(method === 'online') && (
          <motion.div
            key={method}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Reference Number</label>
              <input
                type="text"
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
                placeholder="Enter transaction reference"
                className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                autoFocus
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-6 space-y-3">
        <motion.button
          whileHover={canConfirm ? { scale: 1.01 } : {}}
          whileTap={canConfirm ? { scale: 0.99 } : {}}
          onClick={handleConfirm}
          disabled={!canConfirm}
          className="w-full py-3 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-primary-600 hover:bg-primary-700 text-white shadow-sm"
        >
          {loading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <>
              <Check size={18} />
              Confirm Payment
            </>
          )}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={onCancel}
          disabled={loading}
          className="w-full py-2.5 text-sm font-medium rounded-xl transition-all duration-200 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Cancel
        </motion.button>
      </div>
    </div>
  );
}
